import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Eye } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Swatch } from "@/components/ColorSwatch";
import { SCENE } from "@/lib/categoryAssets";

export default function SavedLooks() {
  const [looks, setLooks] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) { navigate("/signin"); return; }
    if (!user) return;
    api.get("/looks").then(({ data }) => setLooks(data)).catch(() => setLooks([]));
  }, [user, navigate]);

  const del = async (id) => {
    try { await api.delete(`/looks/${id}`); setLooks((l) => l.filter((x) => x.id !== id)); toast.success("Removed."); }
    catch { toast.error("Couldn't remove that look."); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
      <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Saved Looks</p>
      <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-3">Your styled pairings</h1>

      {looks && looks.length === 0 && (
        <div className="border border-[#F5F2EA]/10 bg-[#111111] mt-12 overflow-hidden md:grid md:grid-cols-2">
          <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden bg-[#141414]">
            <img src={SCENE.savedLooks} alt="Curated folded garments" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="p-12 md:p-16 text-center flex flex-col items-center justify-center">
            <p className="font-serif text-2xl text-[#F5F2EA]">Nothing saved yet.</p>
            <p className="font-sans text-sm text-[#8A8170] mt-3">When a pairing feels right, save it — it'll live here.</p>
            <button onClick={() => navigate("/match")} className="mt-8 border border-[#C8A97E] text-[#C8A97E] px-8 py-3 font-sans text-xs tracking-wider uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors">Match a piece</button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        {looks?.map((look) => (
          <div key={look.id} className="border border-[#F5F2EA]/10 bg-[#111111] p-6 group" data-testid={`saved-${look.id}`}>
            <div className="flex items-center gap-3">
              <Swatch hex={look.analysis.primary_color_hex} size="md" />
              <span className="font-serif text-xl text-[#8A8170]">+</span>
              <Swatch hex={look.recommendation.color_hex} size="md" />
            </div>
            <h3 className="font-serif text-2xl text-[#F5F2EA] mt-4">{look.recommendation.color_name}</h3>
            <p className="font-sans text-xs text-[#8A8170] mt-1">with {look.analysis.primary_color_name} {look.analysis.item}</p>
            <div className="flex items-center justify-between mt-5">
              <span className="font-serif text-2xl text-[#C8A97E]">{Number(look.recommendation.score).toFixed(1)}</span>
              <div className="flex items-center gap-3">
                <button onClick={() => { sessionStorage.setItem("moire.lookRequest", JSON.stringify({ source_analysis: look.analysis, recommendation: { color_name: look.recommendation.color_name, color_hex: look.recommendation.color_hex, match_category: look.match_category || "Complete Look" }, occasion: look.occasion || null })); navigate("/look"); }}
                  data-testid={`saved-see-${look.id}`} className="flex items-center gap-1.5 font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] hover:text-[#E5C293]"><Eye size={13} strokeWidth={1.5} /> See The Look</button>
                <button onClick={() => del(look.id)} data-testid={`saved-del-${look.id}`}
                  className="text-[#8A8170] hover:text-[#F5F2EA] transition-colors">
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
